import { supabase } from './supabase';
import type { NewsArticle } from '../types';

/**
 * Favoris du fil News — table `news_favorites`, RLS `user_id = auth.uid()`.
 *
 * Modèle de `services/coverLetter.ts`. Trois opérations seulement : lire les siens, ajouter,
 * retirer. AUCUNE mise à jour : une ligne de favori ne se modifie pas, et la table n'a d'ailleurs
 * pas de policy `update` — un `.update()` échouerait silencieusement en ne touchant aucune ligne.
 *
 * La clé est l'ID SANITY, jamais le slug. Le slug dérive du titre : réécrire un titre le change, et
 * tous les favoris pointant dessus deviendraient orphelins sans que rien ne le signale.
 */

/**
 * Copie minimale conservée au moment du favori.
 *
 * POURQUOI STOCKER UNE COPIE plutôt que l'ID seul : l'API n'expose pas de récupération par lot
 * d'identifiants. L'onglet Favoris ne peut donc PAS re-résoudre un article que la pagination n'a
 * pas encore atteint — un favori posé il y a trois mois se trouve en page sept, que personne n'a
 * chargée. Sans copie, la liste afficherait des lignes vides.
 *
 * C'EST UN REPLI D'AFFICHAGE, PAS LA VÉRITÉ. Si l'article est présent dans le fil chargé, c'est la
 * version fraîche de l'API qui s'affiche : un titre corrigé depuis doit se voir. La copie ne sert
 * que quand l'API ne dit rien.
 */
export function buildSnapshot(article: NewsArticle): Record<string, unknown> {
  return {
    title: article.title,
    excerpt: article.excerpt,
    category: article.category,
    date: article.date,
    readingMinutes: article.readingMinutes,
    slug: article.slug,
    publisher: article.publisher,
    coverImage: article.coverImage,
  };
}

export interface FavoriteRow {
  article_id: string;
  snapshot: Record<string, unknown>;
  created_at: string;
}

export async function listFavorites(): Promise<FavoriteRow[]> {
  const { data, error } = await supabase
    .from('news_favorites')
    .select('article_id, snapshot, created_at')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as FavoriteRow[]) ?? [];
}

/**
 * `on conflict do nothing` : reposer un favori déjà posé n'est pas une erreur. Deux appareils qui
 * se synchronisent, ou un double appui, ne doivent pas produire d'échec — et surtout pas écraser
 * `created_at`, qui ordonne la liste.
 */
export async function addFavorite(article: NewsArticle): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('not_authenticated');

  const { error } = await supabase
    .from('news_favorites')
    .upsert(
      { user_id: user.id, article_id: article.id, snapshot: buildSnapshot(article) },
      { onConflict: 'user_id,article_id', ignoreDuplicates: true },
    );
  if (error) throw error;
}

export async function removeFavorite(articleId: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('not_authenticated');

  const { error } = await supabase
    .from('news_favorites')
    .delete()
    .eq('user_id', user.id)
    .eq('article_id', articleId);
  if (error) throw error;
}
